import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { IApiLink, IValidationRule } from "@/types"
import Button from "components/Button/Button"
import FormField from "components/FormField/FormField"
import useInputNumber from "components/InputNumber/hooks/useInputNumber"
import Modal from "components/Modal/Modal"
import AmountWithCurrency, { IAmountWithCurrencyDto } from "models/AmountWithCurrency"
import InputNumber from 'components/InputNumber/InputNumber'
import { useTenantSettings } from "stores/tenantSettings"
import CustomError from "models/CustomError"
import ValidationService from "services/ValidationService"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import Input from "components/Input/Input"
import Text from "components/Text/Text"
import { INPUT_MAX_LENGTH_DEFAULT } from "config/constants"
import { Flex } from "components/Flex"

interface IEditEstimatedNetSalePriceModalProps {
    onClose: () => void
    data: {
        link: IApiLink | null,
        referenceNumberSale: string,
        estimatedNetSalePrice?: AmountWithCurrency,
        estimatedSDC?: AmountWithCurrency,
        comment?: string,
        isNetSalePriceEditable?: boolean,
        automaticNetSalePrice?: AmountWithCurrency
    }
    onSave: (value: {
        link: IApiLink | null,
        referenceNumberSale: string,
        estimatedNetSalePrice: IAmountWithCurrencyDto | null,
        estimatedSDC: IAmountWithCurrencyDto | null,
        comment: string | null
    }) => Promise<void>

}
const EditEstimatedNetSalePriceModal: FC<IEditEstimatedNetSalePriceModalProps> = ({ onClose, data, onSave }) => {

    const { t } = useTranslation()
    const { tenantSettings } = useTenantSettings()

    const [isSubmitted, setIsSubmitted] = useState(false)
    const [isUpdateLoading, setIsUpdateLoading] = useState(false)
    const [isResetLoading, setIsResetLoading] = useState(false)

    const isLoading = isUpdateLoading || isResetLoading

    const [estimatedNetSalePrice, estimatedNetSalePriceNumber, setEstimatedNetSalePrice] = useInputNumber(
        data.estimatedNetSalePrice?.amount ?? data.automaticNetSalePrice?.amount ?? ""
    )
    const [estimatedSDC, estimatedSDCNumber, setEstimatedSDC] = useInputNumber(data.estimatedSDC?.amount ?? "")
    const [comment, setComment] = useState<string>(data.comment ?? "")

    const validationErrors = useMemo((): { [x: string]: CustomError } => {
        // Can edit when there's no automatic price OR when there's already a manual estimate
        const canEditNetSalesPrice = data.isNetSalePriceEditable && (!data.automaticNetSalePrice || !!data.estimatedNetSalePrice)
        const hasValidNetSalesPrice = canEditNetSalesPrice && estimatedNetSalePriceNumber && estimatedNetSalePriceNumber > 0
        const hasAutomaticNetSalesPrice = !!data.automaticNetSalePrice
        const hasValidSDC = estimatedSDCNumber && estimatedSDCNumber > 0
        const hasAtLeastOneValue = hasValidNetSalesPrice || hasValidSDC || hasAutomaticNetSalesPrice

        const validationFields: [string, IValidationRule, any][] = [
            ["comment", { required: true }, comment],
        ]

        // Add conditional validation for estimated net sales price (only if editable)
        if (canEditNetSalesPrice && estimatedNetSalePrice) {
            validationFields.push(["estimatedNetSalePrice", { gt: 0, type: "decimal" }, estimatedNetSalePrice])
        }

        // Add conditional validation for estimated SDC
        if (estimatedSDC) {
            validationFields.push(["estimatedSDC", { gt: 0, type: "decimal" }, estimatedSDC])
        }

        const errors = ValidationService.validateMultiple(validationFields)

        // Add custom validation for requiring at least one value
        if (!hasAtLeastOneValue) {
            const errorMessage = canEditNetSalesPrice
                ? "At least one of 'Estimated Net Sales Price' or 'Estimated SDC' must be filled."
                : "'Estimated SDC' must be filled."
            errors.atLeastOneValue = { message: errorMessage } as CustomError
        }

        return errors
    }, [comment, estimatedNetSalePrice, estimatedNetSalePriceNumber, estimatedSDC, estimatedSDCNumber, data.isNetSalePriceEditable, data.automaticNetSalePrice, data.estimatedNetSalePrice])


    const handleSave = async () => {
        setIsSubmitted(true)
        const currency = data.estimatedNetSalePrice?.currency ?? data.estimatedSDC?.currency ?? tenantSettings?.defaultCurrency

        if (Object.keys(validationErrors).length > 0 || !currency || !comment) {
            return
        }

        const canEditNetSalesPrice = data.isNetSalePriceEditable && (!data.automaticNetSalePrice || !!data.estimatedNetSalePrice)
        const hasValidNetSalesPrice = canEditNetSalesPrice && estimatedNetSalePriceNumber && estimatedNetSalePriceNumber > 0
        const hasValidSDC = estimatedSDCNumber && estimatedSDCNumber > 0

        if (!hasValidNetSalesPrice && !hasValidSDC) {
            return
        }

        try {
            setIsUpdateLoading(true)

            await onSave({
                link: data.link,
                referenceNumberSale: data.referenceNumberSale,
                estimatedNetSalePrice: hasValidNetSalesPrice ? { amount: estimatedNetSalePriceNumber, currency } : null,
                estimatedSDC: hasValidSDC ? { amount: estimatedSDCNumber, currency } : null,
                comment
            })
            onClose()
        } finally {
            setIsUpdateLoading(false)
        }
    }
    const handleReset = async () => {
        try {
            setIsResetLoading(true)
            await onSave({
                link: data.link,
                referenceNumberSale: data.referenceNumberSale,
                estimatedNetSalePrice: null,
                estimatedSDC: null,
                comment: null
            })
            onClose()

        } finally {
            setIsResetLoading(false)
        }
    }

    return (
        <Modal
            width="630px"
            onClose={onClose}
            title={t('fairValueEvaluation.editEstimates')}
            footer={
                <Flex justifyContent="flex-end">
                    <Button
                        className="ml-2"
                        label={t('common.cancel')}
                        theme="secondary"
                        disabled={isLoading}
                        onClick={onClose}
                    />
                    <Button
                        className="ml-2"
                        label={t('fairValueEvaluation.resetPrice')}
                        theme="secondary"
                        onClick={handleReset}
                        loading={isResetLoading}
                        disabled={isLoading}
                    />
                    <Button
                        className="ml-2"
                        label={t('common.save')}
                        onClick={handleSave}
                        loading={isUpdateLoading}
                        disabled={isLoading}
                    />
                </Flex>
            }
        >
            <FormField label={t('fairValueEvaluation.estimatedNetSalesPrice')}>
                <InputNumber
                    value={estimatedNetSalePrice}
                    onChange={setEstimatedNetSalePrice}
                    minDecimals={2}
                    maxDecimals={2}
                    isSubmitted={isSubmitted}
                    disabled={isLoading || !data.isNetSalePriceEditable || (!!data.automaticNetSalePrice && !data.estimatedNetSalePrice)}
                    after={<div className={`px-2 ${(isLoading || !data.isNetSalePriceEditable || (!!data.automaticNetSalePrice && !data.estimatedNetSalePrice)) ? 'color-grey-500' : ''}`}>{data.estimatedNetSalePrice?.currency || data.automaticNetSalePrice?.currency || tenantSettings?.defaultCurrency}</div>}
                />
                {data.automaticNetSalePrice && !data.estimatedNetSalePrice && (
                    <Text className="text-xs color-grey-500 mt-1">
                        {t('fairValueEvaluation.notEditableAutomatic')}
                    </Text>
                )}
                {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'estimatedNetSalePrice') &&
                    <ValidationMessage>{validationErrors.estimatedNetSalePrice.message}</ValidationMessage>
                }
            </FormField>
            <FormField label={t('fairValueEvaluation.estimatedSDC')}>
                <InputNumber
                    value={estimatedSDC}
                    onChange={setEstimatedSDC}
                    minDecimals={2}
                    maxDecimals={2}
                    isSubmitted={isSubmitted}
                    disabled={isLoading}
                    after={<div className="px-2">{data.estimatedSDC?.currency || data.estimatedNetSalePrice?.currency || tenantSettings?.defaultCurrency}</div>}
                />
                {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'estimatedSDC') &&
                    <ValidationMessage>{validationErrors.estimatedSDC.message}</ValidationMessage>
                }
            </FormField>
            {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'atLeastOneValue') &&
                <ValidationMessage>{validationErrors.atLeastOneValue.message}</ValidationMessage>
            }
            <FormField label={t('fairValueEvaluation.comments')} className='mb-4 mt-4'>

                <Input
                    placeholder={t("fairValueEvaluation.commentsPlaceholder")}
                    type="textarea"
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    maxLength={INPUT_MAX_LENGTH_DEFAULT}
                />

                {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'comment') &&
                    <ValidationMessage>{validationErrors.comment.message}</ValidationMessage>
                }
            </FormField>


        </Modal>
    )
}

export default EditEstimatedNetSalePriceModal
